/**
 * @author       luis.orihuela@zumper.com (Luis Orihuela)
 * @copyright    Copyright (c) 2023, Zumper
 */

import { ShortTermDateRangeInput } from '@zcode-ui/controls'
import { ChevronLeft, Close, calendarGray600 } from '@zcode-ui/icons'
import {
  Button,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  cn,
  useDisclosure,
} from '@zcode-ui/shadz-ui'
import { a11yButtonHandler } from '@zcode/a11y-utils'
import { addDays, addMonths, format, parse, subDays } from 'date-fns'
import React, { useCallback, useEffect, useRef, useState, startTransition } from 'react'
import { Input, type InputProps } from '../Input/Input'
import {
  BaseInputWithPopover,
  DownArrow,
  type OptionType,
} from '../InputWithPopover/BaseInputWithPopover'
import { OpenSelect } from '../InputWithPopover/OpenSelect'

export interface MoveInDateInputProps
  extends Pick<InputProps, 'isInvalid' | 'isRequired' | 'isDefault'> {
  error?: string
  isMobile: boolean
  moveInDate: Date | string | undefined
  moveInText: Exclude<(typeof options)[number]['displayValue'], ''> | undefined
  onSelectMoveInDate: (
    date: Date,
    text?: Exclude<(typeof options)[number]['displayValue'], ''>
  ) => void
}

export const DATE_FORMAT = 'M/dd/yyyy' as const

const getDateBeforeToday = (days?: number) => subDays(new Date(), days ?? 1)
export function formatDate(date?: undefined): undefined
export function formatDate(date: Date): string
export function formatDate(date?: Date): string | undefined
export function formatDate(date?: Date) {
  return date ? format(date, DATE_FORMAT) : undefined
}
const getDateAfterToday = (days?: number, now: number = Date.now()) =>
  addDays(new Date(now), days ?? 1)

export const getMoveInDateFromMoveInText = (
  moveInText: Exclude<Parameters<MoveInDateInputProps['onSelectMoveInDate']>[1], undefined>,
  now: number
) => {
  for (const option of options) {
    if (option.displayValue === moveInText) {
      return getDateAfterToday(option.value, now)
    }
  }
}

const PopoverHeader = ({
  children,
  isCalendarOpen,
  onClick,
}: {
  children: React.ReactNode
  isCalendarOpen: boolean
  onClick: () => void
}) => {
  return (
    <p
      className={cn(
        'text-center text-gray-800 label14Caps',
        isCalendarOpen && 'grid grid-cols-[1fr_auto_1fr] items-center'
      )}
    >
      {isCalendarOpen && (
        <Button
          autoFocus
          className="justify-self-start ml-2"
          data-testid="popover-back-button"
          onClick={onClick}
          variant="ghost"
        >
          <ChevronLeft />
        </Button>
      )}
      {children}
    </p>
  )
}

const options = [
  {
    displayValue: 'ASAP',
    label: 'As soon as possible',
    value: 14,
  },
  {
    displayValue: 'In the next 30 days',
    label: 'In the next 30 days',
    value: 30,
  },
  {
    displayValue: 'Later this year',
    label: 'Later this year',
    className: 'mb-8',
    value: 60,
  },
  {
    displayValue: '',
    label: (
      <>
        Select a date <img src={calendarGray600} alt="" />
      </>
    ),
    value: 'Select a date',
    className:
      'before:h-px before:bg-gray-150 before:w-full before:block before:absolute before:left-0 before:right-0 before:-top-4 flex items-center justify-between relative',
  },
] as const satisfies readonly OptionType<unknown>[]

const DAYS_OF_WEEK = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

const DaysOfTheWeek = () => {
  return (
    <div className="flex justify-between mb-2 min-w-[324px] capitalize w-full">
      {DAYS_OF_WEEK.map((day, i) => (
        <span
          className="flex grow text-gray-600 text-sm font-regular justify-center"
          key={`day + ${i}`}
        >
          {day}
        </span>
      ))}
    </div>
  )
}

export const MoveInDateInput = ({
  error,
  isDefault,
  isInvalid,
  isMobile,
  isRequired,
  moveInDate,
  moveInText,
  onSelectMoveInDate,
}: MoveInDateInputProps) => {
  const selectedOptionRef = useRef<HTMLButtonElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [value, setValue] = useState(() => {
    if (typeof moveInText === 'string') {
      return moveInText
    }
    if (typeof moveInDate === 'string') {
      if (moveInDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return formatDate(parse(moveInDate, 'yyyy-MM-dd', new Date()))
      }
      return moveInDate
    }
    return formatDate(moveInDate) ?? ''
  })
  useEffect(() => {
    if (moveInText) {
      setValue(moveInText)
    }
  }, [moveInText])
  const [startDate, setStartDate] = useState(() => {
    if (typeof moveInDate === 'object') return moveInDate
    return getDateAfterToday()
  })
  const today = new Date()
  const { isOpen, onClose, onOpen } = useDisclosure()
  const {
    isOpen: isCalendarOpen,
    onClose: onCloseCalender,
    onOpen: onOpenCalendar,
  } = useDisclosure()

  const section: HTMLElement | null =
    typeof document !== 'undefined'
      ? document.querySelector('section.chakra-popover__content')
      : null
  const handleSelect = (option: (typeof options)[number]) => {
    if (option.value !== 'Select a date') {
      onSelectMoveInDate(getDateAfterToday(option.value), option.displayValue)
      handleClose()
      setValue(option.displayValue)
    } else {
      onOpenCalendar()
    }
  }
  // For some reason when the calendar opens or closes, the focus on the popover is lost
  // and you can't close it until you click on the popover again. This aims to solve that.
  useEffect(() => {
    section?.focus()
  }, [isCalendarOpen])

  const ignoreNextFocus = useRef(false)
  const handleClick = (e: Pick<React.SyntheticEvent<HTMLElement>, 'stopPropagation' | 'type'>) => {
    // the input is re-focused when we close the drawer, don't let that trigger a re-open
    if (!ignoreNextFocus.current || e.type === 'click') {
      e.stopPropagation()
      startTransition(() => {
        onOpen()
      })
    }
    if (isCalendarOpen) {
      onCloseCalender()
    }
  }

  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    if (
      e.relatedTarget?.closest('[role="dialog"]') === e.currentTarget.closest('[role="dialog"]')
    ) {
      ignoreNextFocus.current = false
    }
  }, [])

  const handleClose = () => {
    ignoreNextFocus.current = true
    startTransition(() => {
      onClose()
      onCloseCalender()
    })
  }

  const onSelectDate = (date: Date) => {
    setStartDate(date)
    setValue(formatDate(date) ?? '')
    onSelectMoveInDate(date)
    // this allows user to see their selection in the calendar before closing it.
    setTimeout(() => {
      handleClose()
    }, 300)
  }

  return (
    <>
      {isMobile ? (
        <div data-testid="input-drawer" className="w-full">
          <Input
            aria-expanded={isOpen}
            data-testid="move-in-date-mobile"
            error={error}
            isDefault={isDefault}
            isInvalid={isInvalid}
            isRequired={isRequired}
            label="Move-in date"
            onBlur={handleBlur}
            onClick={handleClick}
            onFocus={handleClick}
            onKeyUp={a11yButtonHandler(handleClick)}
            readOnly
            ref={inputRef}
            rightElement={<DownArrow isInverted={isOpen} />}
            rightElementFocusable={false}
            role="combobox"
            value={value}
            width="100%"
          />
          <Drawer open={isOpen} onClose={handleClose}>
            <DrawerContent
              className={cn(
                'z-popover rounded-t-xl p-0 m-0',
                isCalendarOpen ? 'h-[560px]' : 'h-fit'
              )}
              overlayClassname="z-modal"
              style={{ height: undefined }}
            >
              <DrawerHeader className="relative flex flex-col border-b border-solid border-gray-150 px-6 pt-6 pb-0">
                <div className="mb-6 flex justify-center">
                  <Button
                    data-testid={'close-drawer'}
                    variant="ghost"
                    className="left-4 top-4 absolute"
                    onClick={isCalendarOpen ? onCloseCalender : handleClose}
                    tabIndex={2}
                  >
                    {isCalendarOpen ? (
                      <ChevronLeft />
                    ) : (
                      <Close fill="#726F6C" size={10} strokeWidth={1.5} />
                    )}
                  </Button>
                  <DrawerTitle asChild>
                    <span className="p-0 m-0 text-gray-800 text-sm font-body font-extrabold leading-normal tracking-widest">
                      MOVE-IN DATE
                    </span>
                  </DrawerTitle>
                </div>
                {isCalendarOpen ? <DaysOfTheWeek /> : null}
              </DrawerHeader>
              <div className={cn('py-6', isCalendarOpen && 'px-6 overflow-y-auto')}>
                {isCalendarOpen ? (
                  <ShortTermDateRangeInput
                    calendarDisplayNum={3}
                    disableForeverFrom={addMonths(today, 12)}
                    disableForeverTo={getDateBeforeToday()}
                    endDate={startDate}
                    endInputPlaceholder="End"
                    initDate={startDate}
                    isMobileScrollCalendar
                    setStartDate={onSelectDate}
                    showTextInput={false}
                    startDate={startDate}
                    startInputPlaceholder="Start"
                    transparentBackground
                  />
                ) : (
                  <OpenSelect
                    isMobile
                    onSelect={handleSelect}
                    options={options}
                    selectedOptionRef={selectedOptionRef}
                    value={moveInText ?? ''}
                  />
                )}
              </div>
            </DrawerContent>
          </Drawer>
        </div>
      ) : (
        <BaseInputWithPopover
          error={error}
          inputLabel="Move-in date"
          isDefault={isDefault}
          isOpen={isOpen}
          isRequired={isRequired}
          onClick={handleClick}
          onClose={handleClose}
          onKeyDown={handleClick}
          popoverBodyClassName="[>div:first-of-type]:min-w-[276px] [>div:first-of-type]:px-6"
          popoverHeader={
            <PopoverHeader isCalendarOpen={isCalendarOpen} onClick={onCloseCalender}>
              MOVE-IN DATE
            </PopoverHeader>
          }
          popoverHeaderClassName={cn('h-[65px]', isCalendarOpen && 'py-3')}
          value={value}
        >
          {isCalendarOpen ? (
            <ShortTermDateRangeInput
              disableForeverFrom={addMonths(today, 12)}
              disableForeverTo={getDateBeforeToday()}
              endDate={startDate}
              endInputPlaceholder="End"
              initDate={startDate}
              setStartDate={onSelectDate}
              showDayOfWeekHeader={true}
              showTextInput={false}
              singleCalendar={true}
              startDate={startDate}
              startInputPlaceholder="Start"
              transparentBackground
            />
          ) : (
            <OpenSelect
              autoFocus
              isMobile={false}
              onSelect={handleSelect}
              options={options}
              selectedOptionRef={selectedOptionRef}
              value={moveInText ?? ''}
            />
          )}
        </BaseInputWithPopover>
      )}{' '}
    </>
  )
}
